import { CommonModule } from '@angular/common';
import {
    Component,
    EventEmitter,
    inject,
    Input,
    OnChanges,
    OnInit,
    Output,
    signal,
    SimpleChanges,
} from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import type { SelfserviceUpdateEmploymentHistoryDetails } from '@libs/generated-types/selfservice';
import { UsersUserResponse } from '@libs/generated-types/users';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { EmploymentHistoryKind } from 'apps/webapp/src/app/@core/data/recruitment';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { PreEmploymentActivitiesService } from '../../../../../../../services/pre-employment-activities.service';
import { FileType, RepositoryShowFileDialogComponent } from '../../../../../repository-show-file-dialog/repository-show-file-dialog.component';

export type EmploymentHistoryField = keyof SelfserviceUpdateEmploymentHistoryDetails;

@Component({
    selector: 'employment-history',
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        TranslateModule,
        ButtonModule,
        SelectModule,
        InputNumberModule,
        DatePickerModule,
        TooltipModule,
        RepositoryShowFileDialogComponent
    ],
    templateUrl: './employment-history.component.html',
    styleUrls: ['./employment-history.component.scss'],
    standalone: true,
})
export class EmploymentHistoryComponent implements OnChanges, OnInit {
    @Input() form: FormGroup;
    @Input() me: UsersUserResponse | null = null;
    /** Quando il contratto è a tempo indeterminato il campo "giorni di malattia" non va mostrato */
    @Input() isPermanentContract: boolean = false;
    @Input() hiredAtYear: number;
    @Input() userCompany: string;
    @Input() activityId: string | null | undefined;
    @Input() confirmedFields: EmploymentHistoryField[] = [];
    @Output() completed = new EventEmitter<void>();

    translate = inject(TranslateService);
    private readonly messageService = inject(MessageService);
    private readonly preEmploymentActivitiesService = inject(PreEmploymentActivitiesService);

    EmploymentHistoryKind = EmploymentHistoryKind;

    /** Numero massimo di giorni di malattia: giorni di calendario tra il 1 gennaio dell'anno corrente e il giorno precedente la data di assunzione */
    maxSickDays = 0;
    savingField: EmploymentHistoryField | null = null;
    private readonly confirmedFieldNames = new Set<EmploymentHistoryField>();

    hadPreviousEmploymentOptions = [
        { label: this.translate.instant('selfservice-request.employment-history.yes-no.yes'), value: EmploymentHistoryKind.WITH_PREVIOUS_EMPLOYMENT },
        { label: this.translate.instant('selfservice-request.employment-history.yes-no.no'), value: EmploymentHistoryKind.NO_PREVIOUS_EMPLOYMENT },
    ];

    yesNoOptions = [
        { label: this.translate.instant('selfservice-request.employment-history.yes-no.yes'), value: true },
        { label: this.translate.instant('selfservice-request.employment-history.yes-no.no'), value: false },
    ];

    detailFileType = signal<FileType | undefined>(undefined);
    showDetailFileDialog = signal(false);
    fileName = signal<string | undefined>(undefined);
    showHelperDialog = signal(false);
    currentHelperField: string | null = null;

    constructor() { }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['confirmedFields']) {
            this.confirmedFieldNames.clear();
            this.applyConfirmedFields();
        }
    }

    ngOnInit(): void {
        this.maxSickDays = this.computeMaxSickDays();
        this.form.get('kind')?.valueChanges.subscribe(() => this.updateValidatorsForKind());
        this.updateValidatorsForKind();
        this.applyConfirmedFields();
    }

    get hasPreviousEmployment(): boolean {
        return this.form.get('kind')?.value === EmploymentHistoryKind.WITH_PREVIOUS_EMPLOYMENT;
    }

    get showSickDays(): boolean {
        return this.hasPreviousEmployment && !this.isPermanentContract;
    }

    get isSaving(): boolean {
        return this.savingField !== null;
    }

    onChangeKind(): void {
        this.updateValidatorsForKind();
    }

    confirmField(field: EmploymentHistoryField): void {
        if (this.isFieldConfirmed(field)) {
            return;
        }

        const controlName: string = field === 'hasPreviousEmployment' ? 'kind' : field;
        const control = this.form.get(controlName);

        control?.markAsTouched();

        if (!this.activityId || !control || control.invalid) {
            return;
        }

        const kind: EmploymentHistoryKind | null = this.form.get('kind')?.value ?? null;
        if (!kind) {
            this.form.get('kind')?.markAsTouched();
            return;
        }

        const details: SelfserviceUpdateEmploymentHistoryDetails = {
            hasPreviousEmployment: kind,
            ...(field === 'hasPreviousEmployment' ? {} : { [field]: control.value }),
        } as SelfserviceUpdateEmploymentHistoryDetails;

        this.savingField = field;
        this.preEmploymentActivitiesService.updateEmploymentHistorySubmission(this.activityId, {
            employmentHistoryDetails: details,
        }).subscribe({
            next: () => {
                this.confirmedFieldNames.add(field);
                control.disable({ emitEvent: false });
                this.savingField = null;

                if (this.isEmploymentHistoryComplete()) {
                    this.completed.emit();
                    return;
                }

                this.messageService.add({
                    severity: 'success',
                    summary: this.translate.instant('pre-employment-selfservice-dialog.toast-summary-success-message'),
                    detail: this.translate.instant('pre-employment-selfservice-dialog.toast-success-message'),
                });
            },
            error: () => {
                this.savingField = null;
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('pre-employment-selfservice-dialog.toast-summary-error-message'),
                    detail: this.translate.instant('pre-employment-selfservice-dialog.toast-error-message'),
                });
            },
        });
    }

    isFieldSaving(field: EmploymentHistoryField): boolean {
        return this.savingField === field;
    }

    isFieldConfirmed(field: EmploymentHistoryField): boolean {
        return this.confirmedFieldNames.has(field);
    }

    private applyConfirmedFields(): void {
        if (!this.form) {
            return;
        }

        this.confirmedFields.forEach((field) => {
            this.confirmedFieldNames.add(field);
            const controlName: string = field === 'hasPreviousEmployment' ? 'kind' : field;
            this.form.get(controlName)?.disable({ emitEvent: false });
        });
    }

    private updateValidatorsForKind(): void {
        const dependentControls = [
            'sickDaysCount',
            'taxableSocialSecurityAmount',
            'fringeBenefitsAmount',
            'hasSeverancePay',
        ];

        if (this.hasPreviousEmployment) {
            this.form.get('taxableSocialSecurityAmount')?.setValidators([Validators.required, Validators.min(0)]);
            this.form.get('fringeBenefitsAmount')?.setValidators([Validators.required, Validators.min(0)]);
            this.form.get('hasSeverancePay')?.setValidators([Validators.required]);

            if (this.showSickDays) {
                this.form
                    .get('sickDaysCount')
                    ?.setValidators([Validators.required, Validators.min(0), Validators.max(this.maxSickDays)]);
            } else {
                this.form.get('sickDaysCount')?.clearValidators();
                this.form.get('sickDaysCount')?.setValue(null);
            }
        } else {
            dependentControls.forEach((controlName) => {
                this.form.get(controlName)?.clearValidators();
                this.form.get(controlName)?.setValue(null);
            });
        }

        dependentControls.forEach((controlName) => this.form.get(controlName)?.updateValueAndValidity());
    }

    isEmploymentHistoryComplete(): boolean {
        const requiredFields: EmploymentHistoryField[] = ['hasPreviousEmployment'];

        if (this.hasPreviousEmployment) {
            requiredFields.push('taxableSocialSecurityAmount', 'fringeBenefitsAmount', 'hasSeverancePay');

            if (this.showSickDays) {
                requiredFields.push('sickDaysCount');
            }
        }

        return requiredFields.every((field) => this.confirmedFieldNames.has(field));
    }

    private computeMaxSickDays(): number {
        if (!this.me?.hiredAt) {
            return 0;
        }

        const hireDate = new Date(this.me.hiredAt);
        const startOfYear = new Date(hireDate.getFullYear(), 0, 1);
        const dayBeforeHire = new Date(hireDate);
        dayBeforeHire.setDate(dayBeforeHire.getDate() - 1);

        const msPerDay = 1000 * 60 * 60 * 24;
        const days = Math.floor((dayBeforeHire.getTime() - startOfYear.getTime()) / msPerDay) + 1;

        return Math.max(0, days);
    }

    getHelperImage() {
        switch (this.currentHelperField) {
            case 'fringeBenefitsAmount':
                return window.location.origin + '/policy/help_compensi_in_natura.jpg';
            case 'taxableSocialSecurityAmount':
                return window.location.origin + '/policy/help_imponibile_previdenziale.jpg';
            default:
                return '';
        }
    }

    openHelper(helperField: EmploymentHistoryField): void {
        this.currentHelperField = helperField;
        this.detailFileType.set(FileType.Image);
        this.fileName.set(this.getHelperImage());
        this.showHelperDialog.set(true);
    }

    onCloseShowHelperDialog(): void {
        this.showHelperDialog.set(false);
        this.detailFileType.set(undefined);
    }
}
